import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';
import { Calendar, Dumbbell, Lightbulb, TrendingUp, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { coachDashboard, connectedTraining } from '../../lib/api';
import { useAuth, useFeatureFlag } from '../../context/AuthContext';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CoachFeedbackCard } from '../../components/feedback/CoachFeedbackCard';

// ─────────────────────────────────────────────
// Helper: format ISO date to short locale label
// ─────────────────────────────────────────────
const localeFor = (lang) => (lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-GB');
const fmtDate = (iso, lang) =>
  new Date(iso).toLocaleDateString(localeFor(lang), { day: '2-digit', month: '2-digit' });

// Module-level chart tick config — extracted to avoid re-allocating the same
// object on every render of the dynamics chart (stable prop identity).
const CHART_TICK_STYLE = { fontSize: 11 };

const extractApiMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

const showForbiddenOrDefault = (error, fallback) => {
  if (error?.response?.status === 403) {
    toast.error(extractApiMessage(error, 'Access denied'));
    return;
  }
  toast.error(fallback);
};

const TARGET_DEVICE_TYPES = ['', 'treadmill', 'stationary_bike', 'elliptical', 'rowing_machine', 'smart_trainer', 'other'];
const WORKOUT_TYPES = [
  'endurance',
  'recovery',
  'interval',
  'warmup',
  'cooldown',
  'strength_conditioning',
  'mobility',
  'tennis_specific_conditioning',
];

const emptyPrescriptionForm = () => ({
  title: '',
  purpose: '',
  scheduled_for: '',
  target_device_type: '',
  workout_type: 'endurance',
  safety_notes: '',
  segment_name: '',
  segment_duration_sec: '',
  segment_distance_m: '',
  target_speed: '',
  target_incline: '',
  target_resistance: '',
  target_cadence: '',
  target_power: '',
  target_heart_rate_zone: '',
  instruction: '',
  warning_cue: '',
});

const optionalNumber = (value) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// ─────────────────────────────────────────────
// Sub-component: Athlete list sidebar
// ─────────────────────────────────────────────
function AthleteList({ athletes, selectedId, onSelect, loading }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (!athletes.length) {
    return <p className="text-text-muted text-center py-8">{t('coach.noAthletes')}</p>;
  }

  return (
    <div className="space-y-2">
      {athletes.map((a) => (
        <button
          key={a.id}
          onClick={() => onSelect(a)}
          data-testid={`select-athlete-${a.id}`}
          className={`w-full p-3 rounded-lg text-left transition-all ${
            selectedId === a.id
              ? 'bg-primary text-white'
              : 'bg-slate-50 text-text-primary hover:bg-slate-100'
          }`}
        >
          <p className="font-medium">{a.name}</p>
          <p className={`text-xs mt-0.5 ${selectedId === a.id ? 'text-white/70' : 'text-text-muted'}`}>
            {a.total_trainings} {t('coach.trainingsShort')} · {a.avg_duration_min} {t('coach.avgMin')}
          </p>
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// Sub-component: Progress panel
// ─────────────────────────────────────────────
function ProgressPanel({ progress, loading }) {
  const { t, i18n } = useTranslation();

  if (loading) return <Skeleton className="h-64 w-full rounded-lg" />;
  if (!progress) return null;

  const serveSpeedKey = t('coach.serveSpeed');
  const accuracyKey = t('coach.accuracy');

  const chartData = (progress.stat_history || []).map((s) => ({
    date: fmtDate(s.date, i18n.language),
    [serveSpeedKey]: s.serve_speed ?? undefined,
    [accuracyKey]: s.accuracy ?? undefined,
  }));

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: t('coach.trainingsCount'), value: progress.total_trainings },
          { label: t('coach.totalMinutes'), value: progress.total_duration_min },
          { label: t('coach.avgServeSpeed'), value: progress.avg_serve_speed ?? '—' },
          { label: t('coach.avgAccuracy'), value: progress.avg_accuracy ?? '—' },
        ].map(({ label, value }) => (
          <Card key={label}>
            <CardContent className="pt-4">
              <p className="text-2xl font-semibold">{value}</p>
              <p className="text-xs text-text-muted mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dynamics chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-basetracking-tight">{t('coach.dynamics30')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={CHART_TICK_STYLE} />
                <YAxis tick={CHART_TICK_STYLE} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey={serveSpeedKey}
                  stroke="#2563eb"
                  dot={false}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey={accuracyKey}
                  stroke="#16a34a"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent trainings table */}
      {progress.recent_trainings && progress.recent_trainings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-basetracking-tight">{t('coach.recentTrainings')}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="table w-full text-sm">
              <thead>
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('common.type')}</th>
                  <th>{t('coach.trainingMin')}</th>
                  <th>{t('coach.coachComment')}</th>
                </tr>
              </thead>
              <tbody>
                {progress.recent_trainings.map((tr) => (
                  <tr key={tr.id}>
                    <td>{fmtDate(tr.date, i18n.language)}</td>
                    <td>
                      <Badge variant="outline">{tr.type}</Badge>
                    </td>
                    <td>{tr.duration}</td>
                    <td className="text-text-muted">{tr.coach_comment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Sub-component: AI Recommendations panel
// ─────────────────────────────────────────────
function RecommendationsPanel({ athleteId, loading: parentLoading }) {
  const { t } = useTranslation();
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!athleteId) return;
    setRecs(null);
    setLoading(true);
    coachDashboard
      .getAthleteRecommendations(athleteId)
      .then((res) => setRecs(res.data))
      .catch((error) => showForbiddenOrDefault(error, t('coach.loadRecsError')))
      .finally(() => setLoading(false));
  }, [athleteId, t]);

  if (parentLoading || loading) return <Skeleton className="h-40 w-full rounded-lg" />;
  if (!recs) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-basetracking-tight flex items-center gap-2">
          <Lightbulb size={16} />
          {t('coach.aiRecommendations')}
          <Badge variant="outline" className="ml-auto text-xs font-normal">
            {recs.source === 'openai' ? 'GPT-4o mini' : t('coach.recommendations')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {(recs.recommendations || []).map((r, i) => (
            <li key={`${r}-${i}`} className="flex gap-2 text-sm">
              <span className="text-primary font-bold">•</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
        {recs.fallback_reason && (
          <p className="mt-3 text-xs text-text-muted">
            fallback: {recs.fallback_reason}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// Sub-component: Schedule panel
// ─────────────────────────────────────────────
function SchedulePanel({ schedule, loading }) {
  const { t, i18n } = useTranslation();

  if (loading) return <Skeleton className="h-40 w-full rounded-lg" />;

  if (!schedule.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-text-muted">
          {t('coach.noUpcomingSessions')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {schedule.map((item) => (
        <Card key={item.id}>
          <CardContent className="pt-4 flex items-start justify-between gap-4">
            <div>
              <p className="font-medium">{item.title}</p>
              <p className="text-xs text-text-muted mt-0.5">
                {new Date(item.date).toLocaleDateString(localeFor(i18n.language), {
                  day: '2-digit',
                  month: 'long',
                })}{' '}
                · {item.start_time}–{item.end_time} · {item.location}
              </p>
            </div>
            <div className="text-right shrink-0">
              <Badge>{item.session_type}</Badge>
              <p className="text-xs text-text-muted mt-1">
                {item.enrolled_count}/{item.max_participants}
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────
function WorkoutPrescriptionForm({ athlete }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyPrescriptionForm);
  const [saving, setSaving] = useState(false);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!athlete?.id) return;

    const segment = {
      order: 1,
      name: form.segment_name.trim() || form.title.trim(),
      duration_sec: optionalNumber(form.segment_duration_sec),
      distance_m: optionalNumber(form.segment_distance_m),
      target_speed: optionalNumber(form.target_speed),
      target_incline: optionalNumber(form.target_incline),
      target_resistance: optionalNumber(form.target_resistance),
      target_cadence: optionalNumber(form.target_cadence),
      target_power: optionalNumber(form.target_power),
      target_heart_rate_zone: form.target_heart_rate_zone.trim() || undefined,
      instruction: form.instruction.trim() || undefined,
      warning_cue: form.warning_cue.trim() || undefined,
      requires_user_confirmation_before_control: true,
    };

    setSaving(true);
    try {
      await connectedTraining.createWorkoutPrescription({
        athlete_id: athlete.id,
        title: form.title.trim(),
        purpose: form.purpose.trim() || undefined,
        scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : undefined,
        target_device_type: form.target_device_type || undefined,
        workout_type: form.workout_type,
        safety_notes: form.safety_notes.trim() || undefined,
        status: 'assigned',
        segments: segment.name ? [segment] : [],
      });
      toast.success(t('schedule.workoutPrescriptionCreateSuccess', 'Workout program assigned'));
      setForm(emptyPrescriptionForm());
    } catch (error) {
      showForbiddenOrDefault(error, t('schedule.workoutPrescriptionCreateError', 'Failed to assign workout program'));
    } finally {
      setSaving(false);
    }
  };

  if (!athlete) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-basetracking-tight flex items-center gap-2">
          <Dumbbell size={16} />
          {t('schedule.assignWorkoutProgram', 'Assign workout program')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold leading-6 text-amber-800 ring-1 ring-amber-100">
            {t('schedule.workoutPrescriptionSafetyNote', 'Equipment control is not enabled. The athlete follows the program manually using the prompts.')}
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1 text-sm font-semibold text-text-primary">
              <span>{t('schedule.programTitle', 'Title')}</span>
              <input className="input w-full" value={form.title} onChange={(event) => updateField('title', event.target.value)} required />
            </label>
            <label className="space-y-1 text-sm font-semibold text-text-primary">
              <span>{t('schedule.scheduledFor', 'Scheduled for')}</span>
              <input type="datetime-local" className="input w-full" value={form.scheduled_for} onChange={(event) => updateField('scheduled_for', event.target.value)} />
            </label>
            <label className="space-y-1 text-sm font-semibold text-text-primary">
              <span>{t('schedule.targetDevice', 'Target device')}</span>
              <select className="input w-full" value={form.target_device_type} onChange={(event) => updateField('target_device_type', event.target.value)}>
                {TARGET_DEVICE_TYPES.map((option) => (
                  <option key={option || 'none'} value={option}>
                    {option ? option.replace(/_/g, ' ') : t('common.notSet', 'Not set')}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm font-semibold text-text-primary">
              <span>{t('schedule.workoutType', 'Workout type')}</span>
              <select className="input w-full" value={form.workout_type} onChange={(event) => updateField('workout_type', event.target.value)}>
                {WORKOUT_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {option.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block space-y-1 text-sm font-semibold text-text-primary">
            <span>{t('schedule.programPurpose', 'Purpose')}</span>
            <textarea className="input w-full" rows={2} value={form.purpose} onChange={(event) => updateField('purpose', event.target.value)} />
          </label>

          <label className="block space-y-1 text-sm font-semibold text-text-primary">
            <span>{t('schedule.safetyNotes', 'Safety notes')}</span>
            <textarea className="input w-full" rows={2} value={form.safety_notes} onChange={(event) => updateField('safety_notes', event.target.value)} />
          </label>

          <div className="rounded-lg border border-slate-200 p-4">
            <p className="mb-3 text-sm font-semibold text-text-primary">{t('schedule.segment', 'Segment')}</p>
            <div className="grid gap-3 md:grid-cols-2">
              <input className="input w-full" placeholder={t('schedule.segmentName', 'Segment name')} value={form.segment_name} onChange={(event) => updateField('segment_name', event.target.value)} />
              <input type="number" min="0" className="input w-full" placeholder={t('schedule.durationSec', 'Duration, sec')} value={form.segment_duration_sec} onChange={(event) => updateField('segment_duration_sec', event.target.value)} />
              <input type="number" min="0" className="input w-full" placeholder={t('schedule.distanceM', 'Distance, m')} value={form.segment_distance_m} onChange={(event) => updateField('segment_distance_m', event.target.value)} />
              <input className="input w-full" placeholder={t('schedule.targetSpeed', 'Target speed')} value={form.target_speed} onChange={(event) => updateField('target_speed', event.target.value)} />
              <input className="input w-full" placeholder={t('schedule.targetIncline', 'Target incline')} value={form.target_incline} onChange={(event) => updateField('target_incline', event.target.value)} />
              <input className="input w-full" placeholder={t('schedule.targetResistance', 'Target resistance')} value={form.target_resistance} onChange={(event) => updateField('target_resistance', event.target.value)} />
              <input className="input w-full" placeholder={t('schedule.targetCadence', 'Target cadence')} value={form.target_cadence} onChange={(event) => updateField('target_cadence', event.target.value)} />
              <input className="input w-full" placeholder={t('schedule.targetPower', 'Target power')} value={form.target_power} onChange={(event) => updateField('target_power', event.target.value)} />
              <input className="input w-full" placeholder={t('schedule.targetHeartRateZone', 'Target heart-rate zone')} value={form.target_heart_rate_zone} onChange={(event) => updateField('target_heart_rate_zone', event.target.value)} />
            </div>
            <textarea className="input mt-3 w-full" rows={2} placeholder={t('schedule.instruction', 'Instruction')} value={form.instruction} onChange={(event) => updateField('instruction', event.target.value)} />
            <textarea className="input mt-3 w-full" rows={2} placeholder={t('schedule.warningCue', 'Warning cue')} value={form.warning_cue} onChange={(event) => updateField('warning_cue', event.target.value)} />
            <label className="mt-3 flex items-center gap-2 text-sm font-semibold text-text-muted">
              <input type="checkbox" checked readOnly className="h-4 w-4 accent-lime-500" />
              {t('schedule.requiresUserConfirmationBeforeControl', 'Requires athlete confirmation before any future device control')}
            </label>
          </div>

          <button type="submit" disabled={saving} className="btn-primary w-full">
            {saving ? t('common.loading') : t('schedule.assignProgram', 'Assign program')}
          </button>
        </form>
      </CardContent>
    </Card>
  );
}

export const CoachDashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const feedbackEnabled = useFeatureFlag('coach_feedback');

  const [athletesList, setAthletesList] = useState([]);
  const [athletesLoading, setAthletesLoading] = useState(true);

  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);

  // Load athletes + schedule on mount
  useEffect(() => {
    coachDashboard
      .getAthletes()
      .then((res) => setAthletesList(res.data))
      .catch(() => toast.error(t('coach.loadAthletesError')))
      .finally(() => setAthletesLoading(false));

    coachDashboard
      .getSchedule()
      .then((res) => setSchedule(res.data))
      .catch(() => toast.error(t('coach.loadScheduleError')))
      .finally(() => setScheduleLoading(false));
  }, [t]);

  const selectAthlete = useCallback((athlete) => {
    setSelectedAthlete(athlete);
    setProgress(null);
    setProgressLoading(true);
    coachDashboard
      .getAthleteProgress(athlete.id)
      .then((res) => setProgress(res.data))
      .catch((error) => showForbiddenOrDefault(error, t('coach.loadProgressError')))
      .finally(() => setProgressLoading(false));
  }, [t]);

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="coach-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-light tracking-tight mb-2">
          {t('coach.dashboard')}
        </h1>
        <p className="text-text-secondary">{t('coach.welcome', { name: user?.name })}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: athlete list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-basetracking-tight flex items-center gap-2">
              <Users size={16} />
              {t('coach.athletes')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AthleteList
              athletes={athletesList}
              selectedId={selectedAthlete?.id}
              onSelect={selectAthlete}
              loading={athletesLoading}
            />
          </CardContent>
        </Card>

        {/* Right: tabbed detail */}
        <div className="lg:col-span-2">
          {!selectedAthlete ? (
            <Tabs defaultValue="schedule">
              <TabsList className="mb-4">
                <TabsTrigger value="schedule">
                  <Calendar size={14} className="mr-1.5" />
                  {t('coach.schedule')}
                </TabsTrigger>
                <TabsTrigger value="programs">
                  <Dumbbell size={14} className="mr-1.5" />
                  {t('schedule.workoutPrograms', 'Workout programs')}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="schedule">
                <SchedulePanel schedule={schedule} loading={scheduleLoading} />
              </TabsContent>
            </Tabs>
          ) : (
            <Tabs defaultValue="progress">
              <TabsList className="mb-4">
                <TabsTrigger value="progress">
                  <TrendingUp size={14} className="mr-1.5" />
                  {t('coach.progress')}
                </TabsTrigger>
                <TabsTrigger value="recommendations">
                  <Lightbulb size={14} className="mr-1.5" />
                  {t('coach.recommendations')}
                </TabsTrigger>
                <TabsTrigger value="schedule">
                  <Calendar size={14} className="mr-1.5" />
                  {t('coach.schedule')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="progress">
                <ProgressPanel progress={progress} loading={progressLoading} />
              </TabsContent>

              <TabsContent value="recommendations">
                <RecommendationsPanel
                  athleteId={selectedAthlete.id}
                  loading={progressLoading}
                />
              </TabsContent>

              <TabsContent value="schedule">
                <SchedulePanel schedule={schedule} loading={scheduleLoading} />
              </TabsContent>

              <TabsContent value="programs">
                <WorkoutPrescriptionForm athlete={selectedAthlete} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {/* Bottom: anonymous feedback aggregator (privacy-sensitive).
          Gated by the `coach_feedback` canary flag (5% rollout). */}
      {feedbackEnabled && (
        <div className="mt-6">
          <CoachFeedbackCard />
        </div>
      )}
    </div>
  );
};
